export default class User {
  public stepInfo: UserEurosStep;

  constructor(totalEurosGained: number) {
    this.stepInfo = new UserEurosStep(totalEurosGained);
  }
}

export const EUserStep = {
  BRONZE: "bronze",
  PLATINE: "platine",
  MASTER: "master",
} as const;

type EUserSteps = (typeof EUserStep)[keyof typeof EUserStep];

enum EUserEurosStep {
  BRONZE = 0,
  PLATINE = 100,
  MASTER = 300,
}

export class UserEurosStep {
  public static AVERAGE_ORDER_EUROS = 15;
  public totalEurosGained: number;
  public currentStep: EUserSteps;
  public nextStepColor: EUserSteps | undefined;
  public stepEuros: number;
  public stepEurosLimit: number;
  public stepEurosLeft: number;
  public ordersLeftLabel: string;
  public nextStepTextClasses: string;
  public currentStepTextClasses: string;

  constructor(totalEurosGained: number) {
    this.totalEurosGained = totalEurosGained;
    this.currentStep = this.computeCurrentStep(totalEurosGained);
    this.stepEuros = this.computeStepEuros(totalEurosGained, this.currentStep);
    this.stepEurosLimit = this.computeStepLimit(this.currentStep);
    this.stepEurosLeft = this.computeStepEurosLeft(
      this.stepEurosLimit,
      this.stepEuros,
    );
    this.nextStepColor = this.computeNextStepColor(this.currentStep);
    this.ordersLeftLabel = this.computeOrdersLeftLabel(this.stepEurosLeft);
    this.nextStepTextClasses = this.computeNextStepTextClasses(
      this.currentStep,
    );
    this.currentStepTextClasses = this.computeCurrentStepTextClasses(
      this.currentStep,
    );
  }

  private computeCurrentStep = (totalEurosGained: number) => {
    if (totalEurosGained >= EUserEurosStep.MASTER) {
      return EUserStep.MASTER;
    }
    if (totalEurosGained >= EUserEurosStep.PLATINE) {
      return EUserStep.PLATINE;
    }
    return EUserStep.BRONZE;
  };

  private computeStepEuros = (
    totalEurosGained: number,
    currentStep: EUserSteps,
  ) => {
    if (currentStep === EUserStep.BRONZE)
      return totalEurosGained - EUserEurosStep.BRONZE;
    if (currentStep === EUserStep.PLATINE)
      return totalEurosGained - EUserEurosStep.PLATINE;
    if (currentStep === EUserStep.MASTER)
      return Math.max(1, totalEurosGained - EUserEurosStep.MASTER);
    return Math.max(1, totalEurosGained);
  };

  private computeStepLimit = (currentStep: EUserSteps) => {
    if (currentStep === EUserStep.BRONZE)
      return EUserEurosStep.PLATINE - EUserEurosStep.BRONZE;
    if (currentStep === EUserStep.PLATINE)
      return EUserEurosStep.MASTER - EUserEurosStep.PLATINE;
    if (currentStep === EUserStep.MASTER)
      return Math.max(1, EUserEurosStep.MASTER - this.totalEurosGained);
    return this.totalEurosGained;
  };

  private computeStepEurosLeft = (
    currentStepLimit: number,
    stepEuros: number,
  ) => currentStepLimit - stepEuros;

  private computeNextStepColor = (currentStep: EUserSteps) => {
    if (currentStep === EUserStep.BRONZE) return EUserStep.PLATINE;
    if (currentStep === EUserStep.PLATINE) return EUserStep.MASTER;
    if (currentStep === EUserStep.MASTER) return EUserStep.MASTER;
  };

  private computeOrdersLeftLabel = (stepEurosLeft: number) => {
    const orders = Math.ceil(stepEurosLeft / UserEurosStep.AVERAGE_ORDER_EUROS);

    return `(~${orders} ${orders > 1 ? "commandes" : "commande"})`;
  };

  private computeNextStepTextClasses = (currentStep: EUserSteps) => {
    if (currentStep === EUserStep.BRONZE) return "text-platine-text";
    if (currentStep === EUserStep.PLATINE) return "text-master-text";
    if (currentStep === EUserStep.MASTER) return "";
    return "";
  };

  private computeCurrentStepTextClasses = (currentStep: EUserSteps) => {
    if (currentStep === EUserStep.BRONZE) return "text-bronze-text";
    if (currentStep === EUserStep.PLATINE) return "text-platine-text";
    if (currentStep === EUserStep.MASTER) return "text-master-text";
    return "";
  };
}
